import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import { Deal, PackageConfig } from '@ca/common/types'
import ModalWide from '@ui/components/Modal/variants/ModalWide'
import { useDeal } from '@order/hooks/useDeal'
import { useCustomDealSettings } from '@order/hooks/useDealSettings'
import {
  useDealSelect,
  useDealTypeModalState,
} from '@order/hooks/useDealTypeModal'
import { BundleType } from '@order/orderTypes'
import GlassesTypeItem from '@screens/configuration/glasses-type/Item'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'

export default function GlassesTypeModal() {
  const { t } = useTranslation(['modals', 'ui', 'common', 'steps'])
  const text = 'configuration:glassestype'
  const config = useRemoteConfig()
  const { close, isOpen, deal, bundle } = useDealTypeModalState()

  const vfDeal = useDeal(deal?.vfDeal ?? undefined)
  const settings = useCustomDealSettings(deal)

  const upgrade = useMemo(() => {
    if (!settings.modalUpsell) {
      return undefined
    }
    if (!config) {
      return undefined
    }
    const vfPackageId = vfDeal?.packages.find(
      (id) => config.packages[id].type === 'vf'
    )
    const vfPackage = vfPackageId && config.packages[vfPackageId]
    const hasOwn = deal?.packages.find(
      (id) => config.packages[id].type === 'vf'
    )

    return {
      deal: vfDeal,
      package: vfPackage,
      hasOwn,
    }
  }, [vfDeal, config, deal?.packages, settings.modalUpsell])

  if (!config?.packages) {
    return null
  }

  const { packages } = config

  return (
    <ModalWide
      open={isOpen}
      close={close}
      title={t(`${text}.header.title`)}
      desc={t(`${text}.header.desc`)}
      hideBottomSpace
      center
      body={
        <View className="gap-8 px-8 pb-8">
          {deal?.packages.map((id) => (
            <Option
              bundle={bundle || 'base'}
              key={id}
              item={packages[id]}
              baseDeal={deal}
            />
          ))}
          {upgrade && !upgrade.hasOwn && upgrade.package && (
            <Option
              bundle={bundle || 'base'}
              deal={upgrade.deal}
              baseDeal={deal}
              key={upgrade.package.id}
              item={upgrade.package}
            />
          )}
        </View>
      }
    />
  )
}

const Option = ({
  bundle,
  item,
  deal,
  baseDeal,
}: {
  bundle: BundleType
  item?: PackageConfig
  deal?: Deal
  baseDeal?: Deal
}) => {
  const { selectVariant, selectUpgrade } = useDealSelect()

  if (!item || !baseDeal) {
    return null
  }

  const onPress = () => {
    if (deal) {
      selectUpgrade(item.id, deal, bundle)
      return
    }
    selectVariant(item.id, baseDeal, bundle)
  }

  return <GlassesTypeItem type={item.type} onPress={onPress} />
}
